import { environment } from './environments/environment';
import { PUBLIC_PATH } from './app/remote-entry/public-path';

// Async version detection that works with or without host meta tags
async function detectVersion() {
  try {
    // Try to fetch version.json from the dashboard's deployed origin (works even in Module Federation remotes)
    // PUBLIC_PATH ensures we fetch from the correct origin when loaded as a remote from Kantar Marketplace
    // Add cache-busting to ensure we always get the latest
    const versionResponse = await fetch(
      `${PUBLIC_PATH}version.json?t=${Date.now()}`,
      {
        cache: 'no-cache'
      }
    );

    if (versionResponse.ok) {
      const versionData = await versionResponse.json();
      return versionData.version !== '1.7.10-dev26102841434'
        ? versionData.version
        : null;
    }
  } catch (error) {
    // Fetch failed - fall back to meta tag
    // eslint-disable-next-line no-console
    console.log('version.json not found, trying meta tag...');
  }

  // Fallback: Try meta tag (works if running standalone)
  const versionMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="app-version"]'
  );
  const deployedVersion = versionMeta?.content;

  if (deployedVersion && deployedVersion !== '1.7.10-dev26102841434') {
    return deployedVersion;
  }

  return null;
}

// Expose version for debugging in dev tools
if (typeof window !== 'undefined') {
  // Get deployed and bundle versions
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const bundleVersion = require('../../../package.json').version;

  detectVersion()
    .then(deployedVersion => {
      const version = deployedVersion || bundleVersion;

      // Debug logging to help diagnose version issues
      if (deployedVersion && deployedVersion !== bundleVersion) {
        // eslint-disable-next-line no-console
        console.warn(
          `%cVersion mismatch: Deployed=${deployedVersion}, Bundle=${bundleVersion}`,
          'color: #ff6b35; font-weight: bold;'
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__DASHBOARD_VERSION__ = version;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__BUNDLE_VERSION__ = bundleVersion;

      // Add version to DOM for easy verification in production (won't be stripped)
      if (document.body) {
        document.body.setAttribute('data-app-version', version);
        document.body.setAttribute('data-bundle-version', bundleVersion);
      }

      // eslint-disable-next-line no-console
      console.log(
        `%cDashboard v${version} [${environment.productType}]`,
        'color: #ff6b35; font-weight: bold; font-size: 14px;'
      );

      // Version mismatch detection: Force reload if cached JS doesn't match deployed version
      // This prevents users from running mixed versions (old cached JS with new index.html)
      const isDev =
        'environmentName' in environment &&
        (
          environment as { environmentName?: string }
        ).environmentName?.toLowerCase() === 'dev';

      if (!isDev && environment.production && deployedVersion) {
        const STORAGE_KEY = 'synd_dashboard_version';
        const RELOAD_FLAG_KEY = 'synd_dashboard_version_reload';

        try {
          const storedVersion = localStorage.getItem(STORAGE_KEY);
          const justReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY);

          if (storedVersion && storedVersion !== version && !justReloaded) {
            // Check if content hashing already loaded the correct bundle
            if (bundleVersion === deployedVersion) {
              // Content hashing worked! Bundle is already up-to-date, just update localStorage
              localStorage.setItem(STORAGE_KEY, version);
              // eslint-disable-next-line no-console
              console.log(
                `%cVersion updated from ${storedVersion} to ${version} via content hash (no reload needed)`,
                'color: #4caf50; font-weight: bold;'
              );
            } else {
              // Bundle is stale (CDN served old cached bundle) - force reload to get new version
              // eslint-disable-next-line no-console
              console.warn(
                `%cStale bundle detected! Stored: ${storedVersion}, Deployed: ${deployedVersion}, Bundle: ${bundleVersion}. Forcing reload...`,
                'color: #ff9800; font-weight: bold; font-size: 12px;'
              );

              // Set flag to prevent infinite reload loop
              sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');

              // Update stored version before reload
              localStorage.setItem(STORAGE_KEY, version);

              // Force hard reload by navigating with cache-busting timestamp
              // This bypasses all browser and CDN caches
              const url = new URL(window.location.href);
              url.searchParams.set('_v', version);
              url.searchParams.set('_t', Date.now().toString());
              window.location.href = url.toString();

              // Stop execution - page will reload
              throw new Error('Reloading due to stale bundle');
            }
          }

          // Clear reload flag if set (successful reload completed)
          if (justReloaded) {
            sessionStorage.removeItem(RELOAD_FLAG_KEY);
            // eslint-disable-next-line no-console
            console.log(
              `%cCache cleared successfully after version update to ${version}`,
              'color: #4caf50; font-weight: bold;'
            );
          }

          // Store current version for future checks
          localStorage.setItem(STORAGE_KEY, version);
        } catch (error) {
          // Don't let version check failures break the app
          // eslint-disable-next-line no-console
          console.error('Version check error:', error);
        }
      }
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Version detection error:', error);
    });
}

function shouldLoadTelemetryScripts(): boolean {
  const environmentName =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((environment as any).environmentName?.toLowerCase() as
      | string
      | undefined) || 'dev';

  if (environmentName !== 'dev') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const forceTelemetryScripts =
      window.localStorage.getItem('forceTelemetryScripts') === 'true';

    if (forceTelemetryScripts) {
      // eslint-disable-next-line no-console
      console.info(
        '[Telemetry] forceTelemetryScripts=true - loading Clarity in Dev'
      );
      return true;
    }
  } catch {
    // Ignore storage access errors and keep default dev behavior.
  }

  // eslint-disable-next-line no-console
  console.info(
    '[Telemetry] Skipping Clarity in Dev environment. Set localStorage.forceTelemetryScripts=true to enable locally.'
  );
  return false;
}

// Expose telemetry configuration for standalone/shared telemetry modules.
if (typeof window !== 'undefined') {
  const trackingHost = environment.api?.trackingHost?.trim();
  const hasResolvedTrackingHost = !!trackingHost && !trackingHost.includes('%');

  if (hasResolvedTrackingHost) {
    const trackingEndpoint = trackingHost.endsWith('/tracking')
      ? trackingHost
      : `${trackingHost}/tracking`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__CLARITY_TRACKING_URL__ = trackingEndpoint;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__PENDO_ENVIRONMENT__ =
    environment.environmentName?.toLowerCase() || 'staging';
}

// Load Clarity for all environments except dev/local
// eslint-disable-next-line @typescript-eslint/no-explicit-any
if (shouldLoadTelemetryScripts()) {
  const script = document.createElement('script');
  // Use absolute paths (starting with /) to ensure proper resolution regardless of base URL
  const scriptType =
    environment.productType === 'Premium'
      ? '/clarity-premium.js'
      : '/clarity-freemium.js';

  // eslint-disable-next-line no-console
  console.log(`Loading Clarity script: ${scriptType}`);

  script.src = scriptType;
  script.async = true;
  script.onload = () => {
    // eslint-disable-next-line no-console
    console.log(`Successfully loaded Clarity script: ${scriptType}`);

    document.dispatchEvent(new CustomEvent('clarity:fullApiReady'));

    // Call consent after Clarity is loaded
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('consent');
      // eslint-disable-next-line no-console
      console.log('Clarity consent called');
    }
  };
  script.onerror = () => {
    // Log error but don't block app initialization
    // eslint-disable-next-line no-console
    console.error(`Failed to load Clarity script: ${scriptType}`);
  };
  document.head.appendChild(script);
}

// PATH_ROUTING_REVERT_2026: Revert this block to restore hash-based shared-view URL normalization.
// Handle legacy hash-based shared view URLs after migrating back to path routing.
// Legacy URL: /app/branddynamics#/shared-view/{id}
// Should be: /app/branddynamics/shared-view/{id}
const currentPath = window.location.pathname;
const currentHash = window.location.hash;

const sharedViewHashMatch = currentHash.match(/^#\/shared-view\/([a-f0-9-]+)/i);

if (sharedViewHashMatch) {
  const sharedViewId = sharedViewHashMatch[1];
  const normalizedPath = currentPath.endsWith('/')
    ? currentPath.slice(0, -1)
    : currentPath;
  const newUrl = `${window.location.origin}${normalizedPath}/shared-view/${sharedViewId}${window.location.search}`;
  window.location.replace(newUrl);
  // Stop bootstrap - page will reload with correct URL
  throw new Error('Redirecting legacy hash-based shared view URL');
}

// eslint-disable-next-line no-console
import('./bootstrap').catch(err => console.error(err));
